<template>
    <div v-if="loading"><v-skeleton-loader type="card" class="loader" /></div>
    <div v-else class="list-item-summary">
        <div class="title-row">
            <div class="title">{{ group.categoryNameNo || group.categoryName }}</div>
            <v-icon icon="mdi-close" @click="emit('closeSummary')" />
        </div>
        <div class="subtitle">{{ group.reasonNo || group.reason }}</div>

        <div class="publishing">
            <div class="title">{{ t('messages.resources.publishingTitle', 'Gjeldende') }}</div>
            <div class="from">
                <span>{{ `${t('messages.resources.from', 'Fra')}: ` }}</span>
                <span>{{ `${ToLocalDate(group.validFrom, 'date')} ${ToLocalDate(group.validFrom, 'time')}` }}</span>
            </div>
            <div class="to">
                <span>{{ `${t('messages.resources.to', 'Til')}: ` }}</span>
                <span>{{ `${ToLocalDate(group.validTo, 'date')} ${ToLocalDate(group.validTo, 'time')}` }}</span>
            </div>

            <div class="title pt-3">{{ t('messages.resources.published', 'Publisert') }}</div>
            <div class="from">
                <span>{{ `${t('messages.resources.from', 'Fra')}: ` }}</span>
                <span>{{ `${ToLocalDate(group.startPublish, 'date')} ${ToLocalDate(group.startPublish, 'time')}` }}</span>
            </div>
            <div class="to">
                <span>{{ `${t('messages.resources.to', 'Til')}: ` }}</span>
                <span>{{ `${ToLocalDate(group.endPublish, 'date')} ${ToLocalDate(group.endPublish, 'time')}` }}</span>
            </div>

            <div class="message-count pt-3">
                {{ t('messages.resources.groupMessageCount', { count: group.messages.length }) }}
            </div>

            <div v-if="canUnpublish" class="message-action single-button pt-3">
                <v-dialog v-model="dialog" max-width="500">
                    <template #activator="{ props: activatorProps }">
                        <v-btn class="published" variant="outlined" v-bind="activatorProps">
                            {{ t('messages.resources.unpublishAll', 'Avpubliser alle') }}
                        </v-btn>
                    </template>
                    <div class="confirmation-dialog">
                        <h2 class="title">{{ t('messages.resources.unpublishAllTitle', 'Avpubliser alle meldinger?') }}</h2>
                        <div class="text">
                            {{ t('messages.resources.unpublishAllConfirm', { count: group.messages.length }) }}
                        </div>
                        <div class="actions">
                            <v-btn color="primary" variant="outlined" @click="dialog = false">
                                {{ t('shared.resources.common.cancel', 'Avbryt') }}
                            </v-btn>
                            <v-btn class="action published" @click="onConfirmUnpublish">
                                {{ t('messages.resources.unpublishAll', 'Avpubliser alle') }}
                            </v-btn>
                        </div>
                    </div>
                </v-dialog>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { MessageListGroupItem } from '../messagesModels';
import { ToLocalDate } from '@/shared/DateFormatHelpers';

defineProps<{
    group: MessageListGroupItem;
    canUnpublish: boolean;
    loading: boolean;
}>();
const emit = defineEmits<{
    (e: 'closeSummary'): void;
    (e: 'unpublishGroup'): void;
}>();

const { t } = useI18n();
const dialog = ref(false);

function onConfirmUnpublish() {
    dialog.value = false;
    emit('unpublishGroup');
}
</script>

<style lang="scss" scoped>
@use '@/shared/variables.scss' as *;

.list-item-summary {
    background-color: $lightGrey;
    padding: 16px 21px;
    margin: 12px 0 12px 12px;
    border-radius: 8px;

    .title-row {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .title {
            font-size: 20px;
            font-weight: bold;
        }
    }

    .subtitle {
        color: $grey;
        margin-bottom: 12px;
    }

    .publishing {
        font-size: 14px;
        padding-top: 24px;

        .title {
            font-size: 16px;
            font-weight: bold;
        }

        .from,
        .to {
            display: flex;
            justify-content: space-between;

            span:first-child {
                font-weight: bold;
            }
        }

        .message-count {
            color: $grey;
        }

        .message-action {
            display: flex;
            justify-content: space-around;
            padding: 32px 0;

            &.single-button {
                justify-content: end;
            }

            button {
                color: $primary;
                text-transform: none;
                font-size: 16px;
                margin-left: 0;
            }

            .published {
                color: $red-darken-4;
            }
        }
    }
}

.loader {
    width: 350px;
    padding: 12px;
}

.confirmation-dialog {
    background-color: $white;
    padding: 16px 24px;
    border-radius: 8px;

    .title {
        font-size: 34px;
        line-height: 34px;
        margin-bottom: 24px;
        text-wrap: nowrap;
    }

    .text {
        font-size: 16px;
    }

    .actions {
        margin-top: 24px;
        display: flex;
        justify-content: center;
        column-gap: 24px;

        .action {
            background-color: $primary;
            color: $white;

            &.published {
                background-color: $red-darken-4;
            }
        }
    }
}
</style>
